import Tesseract, { createWorker, PSM, OEM } from 'tesseract.js';

export interface OCRResult {
  success: boolean;
  text: string;
  confidence: number;
  error?: string;
  processingTime?: number;
  metadata?: {
    language: string;
    pageCount: number;
    symbolsCount: number;
    wordsCount: number;
  };
}

export interface OCROptions {
  language?: string;
  psm?: number;
  oem?: number;
  onProgress?: (progress: number) => void;
}

/**
 * Extract text from a PDF file using OCR (Optical Character Recognition)
 * This is used as a fallback when standard text extraction fails (scanned PDFs, image-based PDFs)
 */
export async function extractTextWithOCR(
  file: File | ArrayBuffer,
  options: OCROptions = {}
): Promise<OCRResult> {
  const startTime = Date.now();
  const {
    language = 'eng',
    psm = PSM.AUTO,
    oem = OEM.LSTM_ONLY,
    onProgress
  } = options;

  let worker: Tesseract.Worker | null = null;

  try {
    console.log('🔍 Starting OCR extraction...');

    // Create Tesseract worker
    worker = await createWorker(language, OEM.LSTM_ONLY, {
      logger: (m) => {
        if (m.status === 'recognizing text' && onProgress) {
          const progress = Math.round(m.progress * 100);
          onProgress(progress);
          console.log(`📊 OCR Progress: ${progress}%`);
        }
      },
    });

    // Set recognition parameters
    await worker.setParameters({
      tessedit_pageseg_mode: psm,
      tessedit_ocr_engine_mode: oem,
    });

    console.log('🔤 Recognizing text from image...');

    // Convert File to image blob if needed
    let imageData: File | ArrayBuffer = file;
    if (file instanceof File) {
      // For PDF files, we need to convert to images first
      if (file.type === 'application/pdf') {
        console.log('📄 Converting PDF to images for OCR...');
        // Use PDF.js to render pages as images
        imageData = await convertPDFToImages(file);
      }
    }

    // Perform OCR
    const { data } = await worker.recognize(imageData as File);

    const processingTime = Date.now() - startTime;

    console.log('✅ OCR extraction completed', {
      confidence: data.confidence,
      textLength: data.text.length,
      processingTime: `${processingTime}ms`
    });

    // Calculate metadata
    const lines = data.text.split('\n').filter(line => line.trim().length > 0);
    const words = data.text.split(/\s+/).filter(word => word.length > 0);
    const symbols = data.text.replace(/\s/g, '').length;

    return {
      success: data.text.length > 50,
      text: data.text,
      confidence: data.confidence,
      processingTime,
      metadata: {
        language,
        pageCount: 1,
        symbolsCount: symbols,
        wordsCount: words.length
      }
    };
  } catch (error: any) {
    console.error('❌ OCR extraction failed:', error);
    return {
      success: false,
      text: '',
      confidence: 0,
      error: error.message || 'OCR extraction failed',
      processingTime: Date.now() - startTime
    };
  } finally {
    // Clean up worker
    if (worker) {
      await worker.terminate();
    }
  }
}

/**
 * Convert PDF pages to images for OCR processing
 */
async function convertPDFToImages(pdfFile: File): Promise<File> {
  // Import PDF.js dynamically to avoid loading it unnecessarily
  const pdfjsLib = await import('pdfjs-dist');

  // Set worker path
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

  console.log('📄 Loading PDF for image conversion...');

  // Load PDF
  const arrayBuffer = await pdfFile.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  console.log(`📄 PDF has ${pdf.numPages} pages`);

  // For now, only process first page to keep it fast
  // In production, you might want to process multiple pages
  const page = await pdf.getPage(1);

  // Set scale for better quality (2x resolution)
  const scale = 2.0;
  const viewport = page.getViewport({ scale });

  // Create canvas
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Could not get canvas context');
  }

  canvas.height = viewport.height;
  canvas.width = viewport.width;

  // Render PDF page to canvas
  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;

  console.log('✅ PDF page rendered to canvas');

  // Convert canvas to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to convert canvas to blob'));
        return;
      }
      // Convert blob to File
      const imageFile = new File([blob], 'pdf-page.png', { type: 'image/png' });
      resolve(imageFile);
    }, 'image/png', 0.95);
  });
}

/**
 * Quick check to determine if a PDF is likely image-based and needs OCR
 */
export async function isPDFImageBased(file: File): Promise<boolean> {
  try {
    const pdfjsLib = await import('pdfjs-dist');
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);

    // Try to get text content
    const textContent = await page.getTextContent();
    const text = textContent.items
      .map((item: any) => item.str)
      .join(' ')
      .trim();

    // If very little text found, it's likely image-based
    const isImageBased = text.length < 50;

    console.log(`📊 PDF image-based check: ${isImageBased ? 'YES' : 'NO'} (text length: ${text.length})`);

    return isImageBased;
  } catch (error) {
    console.error('Error checking if PDF is image-based:', error);
    return false;
  }
}

/**
 * Get supported OCR languages
 */
export function getSupportedLanguages(): string[] {
  return [
    'eng', // English
    'spa', // Spanish
    'fra', // French
    'deu', // German
    'chi_sim', // Simplified Chinese
    'jpn', // Japanese
    'kor', // Korean
    'rus', // Russian
    'ara', // Arabic
    'hin', // Hindi
  ];
}

/**
 * Validate and clean OCR extracted text
 */
export function validateOCRText(text: string): { isValid: boolean; issues: string[] } {
  const issues: string[] = [];

  if (!text || text.trim().length === 0) {
    issues.push('No text extracted');
    return { isValid: false, issues };
  }

  if (text.length < 100) {
    issues.push('Very short text extracted (less than 100 characters)');
  }

  // Check for excessive garbage characters
  const alphanumericRatio = (text.match(/[a-zA-Z0-9]/g) || []).length / text.length;
  if (alphanumericRatio < 0.6) {
    issues.push('High ratio of non-alphanumeric characters (possible OCR errors)');
  }

  // Check for repeated characters (common OCR error)
  const repeatedChars = text.match(/(.)\1{5,}/g);
  if (repeatedChars && repeatedChars.length > 3) {
    issues.push('Multiple sequences of repeated characters detected');
  }

  // Check for reasonable word count
  const words = text.split(/\s+/).filter(w => w.length > 2);
  if (words.length < 20) {
    issues.push('Very few words detected (less than 20)');
  }

  const isValid = issues.length === 0 || (issues.length === 1 && issues[0].includes('short text'));

  return { isValid, issues };
}
